/**
 * Scenario-mode API client (PRP-CALC-1 S8).
 *
 * The interview is data-driven: the backend serves a question graph and the
 * UI walks it generically. Answers (from questions or the free-text parse)
 * share one fact schema; the backend derives engineering inputs and runs
 * the same core calculator that backs Advanced mode.
 */
import type { CalcResponse } from './calcApi';

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://est.anterotrail.com/api/v1';

export type AnswerType =
  | 'choice'
  | 'multichoice'
  | 'number'
  | 'boolean'
  | 'text'
  | 'dimensions'
  | 'lift_list';

export interface QuestionOption {
  value: string;
  label: string;
  stub?: boolean;
}

export interface AskedWhen {
  fact: string;
  in: string[];
}

export interface QuestionNode {
  id: string;
  text: string;
  answer_type: AnswerType;
  options: QuestionOption[];
  asked_when: AskedWhen[];
  feeds?: string | null;
  required: boolean;
  helper_text?: string | null;
  unit?: string | null;
  diagram?: string | null;
}

export interface QuestionGraph {
  calculator: string;
  version: number;
  root: string;
  nodes: QuestionNode[];
}

export type AnswerValue = string | number | boolean | string[];
export type Answers = Record<string, AnswerValue>;

export interface DerivedValue {
  key: string;
  label: string;
  value: number;
  unit: string;
  formula: string;
  basis: string;
  editable: boolean;
  flag?: string | null;
}

export interface ScenarioRunResponse {
  status: 'ok' | 'questions' | 'stub' | 'cannot-derive';
  missing: string[];
  message?: string | null;
  derived: DerivedValue[];
  scenario_narrative: string;
  alternates: string[];
  package?: CalcResponse | null;
}

export function factKey(node: QuestionNode): string {
  return node.feeds || node.id;
}

/** Client-side mirror of the engine's asked_when logic (AND of `in` tests). */
export function isVisible(node: QuestionNode, answers: Answers): boolean {
  return node.asked_when.every((cond) => {
    const v = answers[cond.fact];
    if (v === undefined || v === null) return false;
    const values = Array.isArray(v) ? v : [v];
    return values.some((x) => cond.in.includes(String(x)));
  });
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const doFetch = () =>
    fetch(`${BASE_URL}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...init,
    });
  let res: Response;
  try {
    res = await doFetch();
  } catch {
    // Reason: a backend cold start / network blip surfaces as a bare
    // "Failed to fetch" — one retry absorbs it; if that also fails, say
    // something actionable instead of the browser's raw error.
    try {
      res = await doFetch();
    } catch {
      throw new Error(
        'Could not reach the calculator service — check your connection and try again.',
      );
    }
  }
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body.detail === 'string') detail = body.detail;
    } catch {
      // keep default detail
    }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

export const ScenarioAPI = {
  graph: (calculator: string) =>
    request<QuestionGraph>(`/calc/scenario/${calculator}/graph`),
  run: (
    calculator: string,
    answers: Answers,
    derivedOverrides: Record<string, number> = {},
  ) =>
    request<ScenarioRunResponse>(`/calc/scenario/${calculator}`, {
      method: 'POST',
      body: JSON.stringify({ answers, derived_overrides: derivedOverrides }),
    }),
  parse: (calculator: string, text: string) =>
    request<{ answers: Answers }>(`/calc/scenario/${calculator}/parse`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
};

export default ScenarioAPI;
